// src/pages/portal/Page_therapist/Page_Therapist_Salas/controller/useSalasCatalog.js
import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { ReviContentApi } from "../../../../../api/revi";

// Instancia de la API
const contentApi = new ReviContentApi();

export function useSalasCatalog({ accessToken }) {
  const { t, i18n } = useTranslation("therapist");

  // El idioma entra como dependencia de las cargas, y no es un adorno.
  //
  // Las categorias y las escenas NO son interfaz: son datos que el servidor
  // devuelve ya traducidos segun la cabecera de idioma. Si no se vuelven a
  // pedir al cambiar de idioma, el portal se queda con la copia del idioma
  // anterior y el resultado es media pantalla en frances y los rotulos que
  // vienen de la base de datos en castellano.
  //
  // Se ve al cambiar de idioma sin recargar: con F5 salia bien, y por eso
  // parecia que fallaba la traduccion del servidor cuando lo que fallaba era
  // que nadie la volvia a pedir.
  const idioma = i18n.language;

  // --- Estados ---
  const [categories, setCategories] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);

  // Estados de carga separados para mejor UX
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [scenesLoading, setScenesLoading] = useState(false);

  // 1. Cargar Categorías. Se repiten al cambiar de idioma, no antes.
  //
  // Antes habia aqui un `if (categories.length > 0) return` para "evitar
  // recarga innecesaria". Con un solo idioma no molestaba; con dos, dejaba los
  // rotulos clavados en el idioma con el que se entro.
  //
  // No hace falta ese guardia: `categories` ya no esta en las dependencias, asi
  // que esto solo se dispara si cambia el token o el idioma.
  const loadCategories = useCallback(async () => {
    if (!accessToken) return;

    setCategoriesLoading(true);
    try {
      // El idioma va explicito en la llamada, no se lee de un estado global.
      // Asi la dependencia de abajo es real y no un truco para forzar la
      // recarga, y ademas se pide en el idioma que habia cuando se decidio
      // recargar, no en el que hubiera cuando sale el fetch.
      const catsData = await contentApi.getCategories(
        accessToken,
        true,
        idioma,
      );
      setCategories(catsData || []);
    } catch (e) {
      console.error("Error loading categories:", e);
      message.error(t("salas.avisos.errorCargarCategorias"));
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [accessToken, idioma, t]);

  // 2. Cargar Escenas (Reactivo a selectedCat)
  const loadScenes = useCallback(async () => {
    if (!accessToken) return;

    setScenesLoading(true);
    try {
      // ✅ CORRECCIÓN: Eliminado 'limit: 50'.
      // Ahora usa el default de la API (1000) para traer todo el catálogo.
      const data = await contentApi.getScenes(accessToken, {
        therapyId: selectedCat,
        idioma,
        // limit: 1000 (implícito en la clase API)
      });

      setScenes(data?.scenes || []);
    } catch (e) {
      console.error("Error loading scenes:", e);
      message.error(t("salas.avisos.errorCargarEscenas"));
      setScenes([]);
    } finally {
      setScenesLoading(false);
    }
  }, [accessToken, selectedCat, idioma, t]);

  // --- Efectos ---
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadScenes();
  }, [loadScenes]);

  return {
    categories,
    scenes,
    selectedCat,
    setSelectedCat,
    loading: categoriesLoading || scenesLoading,
  };
}
